import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '../Icon/Icon';
import {
  ACTION_STATUS,
  KEY,
  TIMING,
  LIMITS,
  ARIA_LABELS,
  DEFAULT_MESSAGES,
  getStatusClass,
  getStatusMessage,
  isActionableStatus,
  isFinalStatus,
} from './utils/constants';
import './ActionProposal.scss';

// Debug flag - only log in development
const DEBUG = process.env.NODE_ENV === 'development';

/**
 * ActionProposal Component
 *
 * Generic, reusable component for displaying AI-suggested actions with Run/Skip approval.
 * Can be used anywhere in the application - Profile, Contracts, Prompts, etc.
 * Follows Cascade AI/Windsurf IDE action approval pattern.
 *
 * @component
 * @example
 * // Profile bio update
 * <ActionProposal
 *   proposal={{
 *     id: 'bio-update-1',
 *     type: 'profile_update',
 *     description: 'AI-generated professional bio',
 *     changes: { bio: 'New bio content...' }
 *   }}
 *   onRun={(id) => applyBioUpdate(id)}
 *   onSkip={(id) => console.log('Skipped')}
 * />
 *
 * @example
 * // Terminal command
 * <ActionProposal
 *   proposal={{
 *     id: 'cmd-1',
 *     type: 'command',
 *     description: 'Run database migration',
 *     command: 'npm run migrate:latest'
 *   }}
 *   onRun={(id) => executeCommand(id)}
 *   onSkip={(id) => console.log('Skipped')}
 * />
 *
 * @example
 * // API call
 * <ActionProposal
 *   proposal={{
 *     id: 'api-1',
 *     type: 'api_call',
 *     description: 'Create new contract',
 *     preview: 'POST /api/contracts\n{ title: "New Contract", ... }'
 *   }}
 *   onRun={(id) => createContract(id)}
 *   onSkip={(id) => console.log('Skipped')}
 * />
 *
 * Features:
 * - ✅ Generic and reusable across entire app
 * - ✅ Supports any action type (commands, API calls, data updates, etc.)
 * - ✅ Run/Skip approval buttons with loading states
 * - ✅ Status tracking (pending/running/completed/skipped)
 * - ✅ Copy to clipboard functionality
 * - ✅ Keyboard shortcuts (Enter to run, Escape to skip)
 * - ✅ Accessible (ARIA labels, keyboard navigation)
 * - ✅ Responsive design
 *
 * Props:
 * @param {Object} proposal - The action proposal object
 * @param {string} proposal.id - Unique identifier
 * @param {string} proposal.type - Action type (command, api_call, profile_update, data_change, etc.)
 * @param {string} proposal.description - Action description (optional)
 * @param {string} proposal.command - Shell command to display (optional)
 * @param {string} proposal.preview - Preview text (optional)
 * @param {Object} proposal.changes - Data changes object (optional)
 * @param {Function} onRun - Callback when Run is clicked, receives proposal.id
 * @param {Function} onSkip - Callback when Skip is clicked, receives proposal.id
 * @param {string} status - Current status (pending/running/completed/skipped)
 * @param {boolean} disabled - Disable interaction
 */
export const ActionProposal = ({
  proposal,
  onRun,
  onSkip,
  status = ACTION_STATUS.PENDING,
  disabled = false,
  autoRunMode = 'off', // Passed from Gigabot settings
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasAutoRun, setHasAutoRun] = useState(false);

  // Use useCallback to memoize handleRun to prevent stale closures
  const handleRun = useCallback(async () => {
    if (disabled || !isActionableStatus(status)) return;

    if (DEBUG) {
      console.log(
        '[ActionProposal] handleRun called for proposal:',
        proposal.id
      );
    }

    setIsRunning(true);
    try {
      // Pass the proposal ID to onRun
      if (DEBUG)
        console.log(
          '[ActionProposal] Calling onRun with proposal ID:',
          proposal.id
        );
      await onRun(proposal.id);
      if (DEBUG) console.log('[ActionProposal] onRun completed successfully');
    } catch (error) {
      // Always log errors, even in production
      console.error('[ActionProposal] Failed to run proposal:', error);
    } finally {
      setIsRunning(false);
    }
  }, [disabled, status, proposal.id, onRun]);

  const handleSkip = useCallback(() => {
    if (disabled || !isActionableStatus(status)) return;
    // Pass the proposal ID to onSkip
    onSkip(proposal.id);
  }, [disabled, status, proposal.id, onSkip]);

  const handleCopy = async () => {
    const textToCopy =
      proposal.command ||
      proposal.preview ||
      JSON.stringify(proposal.changes, null, 2);
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), TIMING.COPY_FEEDBACK_DURATION);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || !isActionableStatus(status)) return;

    if (e.key === KEY.ENTER && !e.shiftKey) {
      e.preventDefault();
      handleRun();
    } else if (e.key === KEY.ESCAPE) {
      e.preventDefault();
      handleSkip();
    }
  };

  // Auto-run effect: Automatically run when Turbo mode is enabled
  // Now properly includes all dependencies
  useEffect(() => {
    if (
      autoRunMode === 'turbo' &&
      !hasAutoRun &&
      !isRunning &&
      isActionableStatus(status) &&
      !disabled
    ) {
      setHasAutoRun(true);
      handleRun();
    }
  }, [autoRunMode, status, disabled, hasAutoRun, isRunning, handleRun]);

  // Status class is now handled by imported getStatusClass function

  /**
   * Get content to display in the command box
   * Priority: command > preview > changes > description
   */
  const getCommandDisplay = () => {
    // 1. Direct command (e.g., shell commands)
    if (proposal.command) return proposal.command;

    // 2. Preview text (e.g., API payloads, formatted output)
    if (proposal.preview) return proposal.preview;

    // 3. Changes object (e.g., data updates)
    if (proposal.changes) {
      // Special handling for single-field updates (bio, description, etc.)
      const changeKeys = Object.keys(proposal.changes);
      if (changeKeys.length === 1) {
        const singleValue = proposal.changes[changeKeys[0]];
        // If it's a string and looks like content (not a simple value), show it directly
        if (
          typeof singleValue === 'string' &&
          singleValue.length > LIMITS.MIN_CONTENT_LENGTH_FOR_DISPLAY
        ) {
          return singleValue;
        }
      }

      return Object.entries(proposal.changes)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
    }

    // Fallback to description
    return proposal.description || DEFAULT_MESSAGES.NO_PREVIEW;
  };

  /**
   * Determine if description should be shown separately
   * Hide description if we're showing the same content in the command box
   */
  const shouldShowDescription = () => {
    if (!proposal.description) return false;

    // If command or preview exists, show description separately
    if (proposal.command || proposal.preview) return true;

    // If changes is a single long string being displayed, don't duplicate
    if (proposal.changes) {
      const changeKeys = Object.keys(proposal.changes);
      if (changeKeys.length === 1) {
        const singleValue = proposal.changes[changeKeys[0]];
        if (
          typeof singleValue === 'string' &&
          singleValue.length > LIMITS.MIN_CONTENT_LENGTH_FOR_DISPLAY
        ) {
          return false; // Content is shown in box, don't duplicate
        }
      }
    }

    return true;
  };

  return (
    <div
      className={`action-proposal ${getStatusClass(status)} ${
        disabled ? 'action-proposal--disabled' : ''
      }`}
      onKeyDown={handleKeyDown}
      tabIndex={isActionableStatus(status) ? 0 : -1}
      role='article'
      aria-label={`Action proposal: ${proposal.title}`}
    >
      {/* Description Text - only show if not displaying bio in command box */}
      {proposal.description && shouldShowDescription() && (
        <p className='action-proposal__description'>{proposal.description}</p>
      )}

      {/* Command/Action Box */}
      <div className='action-proposal__command-box'>
        <div className='command-box__header'>
          <span className='command-box__path'></span>
          <div className='command-box__actions'>
            <button
              className='command-box__btn command-box__btn--copy'
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy'}
              aria-label={ARIA_LABELS.COPY_COMMAND}
            >
              <Icon name={copied ? 'Check' : 'Copy'} />
            </button>
            <button
              className='command-box__btn command-box__btn--close'
              onClick={handleSkip}
              title='Close'
              aria-label={ARIA_LABELS.CLOSE_ACTION}
            >
              <Icon name='Close_MD' />
            </button>
          </div>
        </div>
        <div className='command-box__content'>
          <pre>{getCommandDisplay()}</pre>
        </div>

        {/* Action Buttons */}
        {isActionableStatus(status) && (
          <div className='command-box__footer'>
            <button
              className='action-btn action-btn--run'
              onClick={handleRun}
              disabled={disabled || isRunning}
              aria-label={ARIA_LABELS.RUN_ACTION}
            >
              {isRunning ? (
                <>
                  <Icon
                    name='Loading_01'
                    className='btn-icon btn-icon--spinning'
                  />
                  <span>Running...</span>
                </>
              ) : (
                <>
                  <Icon name='Play' className='btn-icon' />
                  <span>Run</span>
                </>
              )}
            </button>

            <button
              className='action-btn action-btn--skip'
              onClick={handleSkip}
              disabled={disabled}
              aria-label={ARIA_LABELS.SKIP_ACTION}
            >
              <span>Skip</span>
            </button>
          </div>
        )}
      </div>

      {/* Status Message */}
      {isFinalStatus(status) && (
        <div
          className={`action-proposal__status-message status-message--${status}`}
        >
          {getStatusMessage(status)}
        </div>
      )}
    </div>
  );
};

export default ActionProposal;
